<template>
  <div class="d-flex align-center justify-space-between bg-white mt-4">
    <div class="d-flex align-center w-100">
      <div class="d-flex align-center">
        <v-icon
          color="primary"
          class="event-type-icon mr-3"
          :icon="iconName"
          size="40"
          data-testid="ticket-preview-icon"
        >
        </v-icon>
        <div>
          <div class="headline6" data-testid="ticket-preview-type">
            {{ $t(`tickets.eventTriggers.${camelCase(ticket.eventTrigger)}`) }}
          </div>
          <div class="d-inline-flex align-center mt-1">
            <slot name="status-selector" />
            <v-chip
              density="comfortable"
              size="x-small"
              color="indigo-instructional"
              variant="flat"
              data-testid="ticket-event-id-chip"
            >
              <span class="coro-label-sm text-primary">{{ ticket.eventId }}</span>
            </v-chip>
            <v-chip
              class="ml-1 text-uppercase"
              v-if="ticket.processed"
              density="comfortable"
              size="x-small"
              color="green-pale"
              variant="flat"
              data-testid="ticket-processed-chip"
            >
              <span class="coro-label-sm text-green-dark">
                {{ $t("general.processedOn") }} {{ processedTime }}
              </span>
              <v-icon icon="$check"></v-icon>
            </v-chip>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex align-center">
      <v-btn
        v-if="showQuickFix"
        rounded
        color="primary"
        class="mr-2 quick-fix-btn"
        data-testid="tickets-page-header-quick-fix-btn"
        @click="$emit('quick-fix')"
        prepend-icon="$quickFix"
      >
        {{ $t("ticketDetails.applyQuickFix") }}
      </v-btn>
      <v-menu v-if="actions.length > 0" location="bottom right" data-testid="ticket-actions-menu">
        <template #activator="{ props }">
          <v-btn rounded variant="outlined" data-testid="tickets-page-actions-btn" v-bind="props">
            {{ $t("general.actions") }}
            <v-icon icon="$triangle"> </v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="action in actions"
            :key="action.name"
            :data-testid="`tickets-page-action-${action.name}`"
            @click="$emit('action-click', action)"
          >
            <v-list-item-title>
              <span>
                {{
                  $t(`ticketDetails.eventActions.${action.name}`, {
                    service: ticket.sections.serviceName
                      ? $t(`services.${ticket.sections.serviceName}`)
                      : "",
                  })
                }}
              </span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { TicketActionWithSeverity, TicketDetails } from "@/_store/tickets/tickets.module";
import camelCase from "lodash/camelCase";
import moment from "moment/moment";
import { ticketQuickFixSectionExists } from "@/_helpers/ticket-helpers";
import { ticketTriggerIcons, ticketTypeIcons } from "@/constants/tickets";

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    actions: {
      type: Array as PropType<TicketActionWithSeverity[]>,
      default: () => [],
    },
  },
  emits: ["action-click", "quick-fix"],
  setup(props) {
    const showQuickFix = computed(() => {
      return !props.ticket.processed && ticketQuickFixSectionExists(props.ticket);
    });

    const processedTime = computed(() => {
      return props.ticket.processedTime ? moment(props.ticket.processedTime).format("ll") : "";
    });

    const iconName = computed(() => {
      const { eventType, eventTrigger } = props.ticket;
      const icon = ticketTypeIcons[eventType] || ticketTriggerIcons[eventTrigger];
      return `$${icon}`;
    });

    return {
      camelCase,
      showQuickFix,
      processedTime,
      iconName,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(.quick-fix-btn) {
  .icon-insights-xs:before {
    color: rgb(var(--v-theme-white)) !important;
  }
}
</style>
